import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PsNote } from "../../../../api/model";
import { useGetPsNotePsList } from "../../../../api/ps-notes/ps-notes";
import { useDefaultOptions } from "../../../../hooks/api";
import * as sessionStorageUtils from '../../../../utils/sessionStorage';
import ErrorComponent from "../../../atoms/ErrorComponent";
import WaitComponent from "../../../atoms/WaitComponent";
import PageNavi from "../../../modules/PageNavi";

interface SortableThProps {
  label: string;
  onClick: () => void;
}

/** ソート可能なテーブル ヘッダー。 */
export function SortableTh({ label, onClick }: SortableThProps) {
  return (
    <th>
      <button type="button" className="text-button" onClick={onClick}>
        {label}
        <i className="fa-solid fa-sort" />
      </button>
    </th>
  );
}

/** ソート情報の型。 */
type SortInfo = {
  key: string;
  inAscendingOrder: boolean;
  // key: string[];
  // inDescendingOrder: boolean;
};

interface Props {
  /** PS バージョン ID。 */
  psVersionId: number;
}

/** PS 改廃案メモ一覧画面の中身。 */
function PsTemporaryNotes(
  {
    psVersionId,
  }: Props
) {

  // i18n 用。
  const {
    t,
    i18n,
  } = useTranslation();

  const defaultOptions = useDefaultOptions(i18n.language);

  /** PS 改廃案メモ一覧取得 (PS 指定) API 呼び出し。 */
  const getPsNotePsListResponse = useGetPsNotePsList(
    psVersionId,
    defaultOptions
  );

  // console.log('getPsNotePsListResponse.data.data:', getPsNotePsListResponse.data?.data);

  /** 1 ページに表示する行数: 50行。 */
  const numberOfRowsPerPage = 50;

  /** 表示中のページ番号。 */
  const [currentPageNumber, setCurrentPageNumber] = useState(
    sessionStorageUtils.get('PsTemporaryNotes.CurrentPageNumber', 1)
  );

  /** ソート情報。 */
  const [sortInfo, setSortInfo] = useState<SortInfo>(
    sessionStorageUtils.get(
      'PsTemporaryNotes.SortInfo',
      {
        key: 'ps_no',
        inAscendingOrder: false,
      }
    )
  );

  /** ソートされた PsNotes。 */
  const sortedPsNotes = useMemo(
    () =>
      [...((getPsNotePsListResponse.data?.data as PsNote[]) ?? [])].sort(
        (psNoteA: PsNote, psNoteB: PsNote) =>
        (
          (((psNoteA as never)[sortInfo.key] < (psNoteB as never)[sortInfo.key]) ? -1 : 1)
          *
          ((sortInfo.inAscendingOrder) ? 1 : -1)
        )
      ),
    [sortInfo, getPsNotePsListResponse.data?.data]
  );

  // console.log('sortInfo', sortInfo);

  // console.log('sortedPsNotes: ', sortedPsNotes);

  /** 表示中のページ分のソートされた PsNotes。 */
  const sortedPsNotesOfCurrentPage = useMemo(
    () => sortedPsNotes.slice(
      numberOfRowsPerPage * (currentPageNumber - 1),
      numberOfRowsPerPage * currentPageNumber
    ),
    [currentPageNumber, sortedPsNotes]
  );

  /** ソート情報を保存する関数。 */
  const changeSortInfo = useCallback(
    (newKey: string) => {
      setSortInfo(
        (previousSortInfo: SortInfo) => {
          const newSortInfo = {
            key: newKey,
            inAscendingOrder: (
              // key が以前と異なる場合、昇順とする。
              (previousSortInfo.key !== newKey)
              ||
              // key が同じ場合、順序を逆にする。
              (!previousSortInfo.inAscendingOrder)
            ),
          };
          sessionStorageUtils.set('PsTemporaryNotes.SortInfo', newSortInfo);
          return newSortInfo;
        }
      );
      setCurrentPageNumber(1);
    },
    []
  );

  // ページ番号を変更する関数。
  const changeCurrentPageNumber = useCallback(
    (nextPageNumber: number) => {
      sessionStorageUtils.set('PsTemporaryNotes.CurrentPageNumber', nextPageNumber);
      setCurrentPageNumber(nextPageNumber);
    },
    []
  );

  return (
    <div
      id='main'
    >
      <div
        className='browsefr'
      >
        {
          (getPsNotePsListResponse.isLoading)
          &&
          (
            <WaitComponent />
          )
        }
        {
          (getPsNotePsListResponse.isError)
          &&
          (
            <ErrorComponent error={getPsNotePsListResponse.error} />
          )
        }

        {
          (sortedPsNotes.length > 0)
          &&
          (
            <>
              <div className="scroll">
                <table className="table22 mt30">
                  <tbody>
                    <tr>
                      {/* ラベルをクリックでソート。 */}
                      <SortableTh
                        label={t('PS管理No.')}
                        onClick={
                          () => { changeSortInfo('ps_no'); }
                        }
                      />
                      <SortableTh
                        label={t('登録日')}
                        onClick={
                          () => { changeSortInfo('created_at'); }
                        }
                      />
                      <SortableTh
                        label={t('該当章')}
                        onClick={
                          () => { changeSortInfo('chapter_name'); }
                        }
                      />
                      <SortableTh
                        label={t('改訂内容（改廃理由）')}
                        onClick={
                          () => { changeSortInfo('description'); }
                        }
                      />
                      <SortableTh
                        label={t('作成者')}
                        onClick={
                          () => { changeSortInfo('create_user_name'); }
                        }
                      />
                    </tr>
                    {/* データ表示部。 */}
                    {
                      sortedPsNotesOfCurrentPage.map(
                        (psNote: PsNote) => (
                          <tr key={psNote.ps_temporary_note_id}>
                            <td>{psNote.ps_no}</td>
                            <td>{psNote.created_at}</td>
                            <td>{psNote.chapter_name}</td>
                            <td>{psNote.description}</td>
                            <td>{psNote.create_user_name}</td>
                          </tr>
                        )
                      )
                    }
                  </tbody>
                </table>
              </div>
              <PageNavi
                current={currentPageNumber}
                pageCount={Math.ceil(sortedPsNotes.length / (numberOfRowsPerPage * 1.0))}
                onChangePage={changeCurrentPageNumber}
              />
            </>
          )
        }

        {/* {
        (getPsNotePsListResponse.isSuccess)
        &&
        (getPsNotePsListResponse.data?.data)
        &&
        (
          (getPsNotePsListResponse.data.data as PsNote[]).map(
            (psNote: PsNote) =>
            (
              <div
                key={psNote.ps_temporary_note_id}
              >
                {psNote.description}
              </div>
            )
          )
        )
      } */}
      </div>
    </div>
  );
}

export default PsTemporaryNotes;
